import React from 'react'
import './style.scss'
import { Link } from 'react-router-dom'
import CX from 'classnames'
import { Classes, HTMLTable, Callout } from '@blueprintjs/core'
import { camelCase } from 'change-case'
import { TABLE_HEADER_COLUMNS, marketAccounts } from './constants'
import EmptyPlaceholder from '../../../components/EmptyPlaceholder'
import { niceNumber } from '../../../utils/number'
import Spinner from '../../../components/Spinner'
import Client from '../../../client'
import { SummaryTableList } from './types'
import { ExternalChannelLink } from '../../../components/ExternalChannelLink'
import { prepareValues } from './helpers'
import Widget from '../../../components/Widget'
import NiceListingStatus from '../../../components/NiceListingStatus'
import { SortDirectionIndicator } from './components/SortDirectionIndicator'
import ListingStates from '../../../constants/listings/ListingStates'
import { newConnection } from '../shared/StatusUpdate'
import { addSequenceToData, sortDataBySortKey, sortDataBySortOrder, getMarketId } from './utils'
import CHANNELS from '../../../constants/Channels'

interface Props {
  listingIds: number[]
  market: string
  listingManagerUrl: string
  marketAccountId: string
}

export const SummaryTable = ({ listingIds, market, listingManagerUrl, marketAccountId }: Props): JSX.Element => {
  const connection = React.useRef<ActionCable.Channel>(null)
  const initialSortedColumn = 'sequence.asc'
  const [sortKey, setSortKey] = React.useState<string>(initialSortedColumn)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [APIError, setAPIError] = React.useState<boolean>(false)
  const [reviewList, setReviewList] = React.useState<SummaryTableList[]>([])
  const { marketName, commaSeparatedIds } = prepareValues(market, listingIds)
  const sortKeyParts = sortKey.split('.')
  const columnName = sortKeyParts[0]

  const fetchData = React.useCallback(async () => {
    const idsCount = listingIds.length
    try {
      setLoading(true)
      setAPIError(false)
      const params = { listingIds: commaSeparatedIds, perPage: idsCount }
      let value
      if (market === marketAccounts.EBAY) {
        const { data } = await Client.v5GetEbayListings(params)
        value = data.ebayListings
      }
      if (market === marketAccounts.SHOPIFY) {
        const shopifyParams = { ...params, marketAccountId }
        const { data } = await Client.v5GetShopifyListings(shopifyParams)
        value = data.shopifyListings
      }
      if (market === marketAccounts.SHOPGOODWILL) {
        const { data } = await Client.v5GetShopgoodwillListings(params)
        value = data.shopgoodwillListings
      }

      // Sorts the `value` by the `listingIds` array.
      // The default sort order returned from the API is descending.
      const initialSortedDataByIds = sortDataBySortOrder(value, listingIds)

      // Added a sequence property to each item in the 'initialSortedDataByIds'
      const addSequenceToSortedData = addSequenceToData(initialSortedDataByIds)

      setReviewList(addSequenceToSortedData)

      // setup action cable connection for polling listings updates
      const marketId = getMarketId(market)
      connection.current = newConnection(marketId, data => {
        const updatedReviewList = addSequenceToSortedData.map(item => {
          if (item.id === parseInt(data.id)) {
            const camelizedData = Object.keys(data).reduce((acc, key) => ({ ...acc, [camelCase(key)]: data[key] }), {})
            return { ...item, ...camelizedData }
          }

          return item
        })

        setReviewList(updatedReviewList)
      })
    } catch {
      setAPIError(true)
    } finally {
      setLoading(false)
    }
  }, [commaSeparatedIds, listingIds, market, marketAccountId])

  React.useEffect(() => {
    fetchData()
  }, [fetchData])

  React.useEffect(() => {
    return () => {
      connection.current.unsubscribe()
      connection.current = null
    }
  }, [])

  const onColumnHeaderSort = React.useCallback(
    e => {
      const parts = e.currentTarget.id.split('-')
      const newSortHeader = parts.length && parts[0]

      if (!newSortHeader) return

      let newSortDir = 'asc'

      if (sortKeyParts[0] === newSortHeader) {
        newSortDir = sortKeyParts[1] === 'asc' ? 'desc' : 'asc'
      }

      const newKey = `${newSortHeader}.${newSortDir}`
      const sortedData = sortDataBySortKey(reviewList, newSortHeader, newSortDir)
      setSortKey(newKey)
      setReviewList(sortedData)
    },
    [sortKeyParts, reviewList]
  )

  if (APIError) {
    return (
      <Callout intent="danger" className="error-callout">
        Error: the review pending listings summary was unable to load{' '}
        <span className="retry-link" onClick={fetchData}>
          Retry
        </span>
      </Callout>
    )
  }

  return (
    <Widget title="Review Summary">
      <div className="summary-table-container">
        {loading && <Spinner />}
        {!loading && (
          <HTMLTable>
            <thead>
              <tr>
                <th
                  id="sequence-column"
                  onClick={onColumnHeaderSort}
                  className={CX('column-header', columnName === 'sequence' && 'selected')}
                >
                  <SortDirectionIndicator header="sequence" sortKey={sortKey} />
                </th>
                {TABLE_HEADER_COLUMNS.map(item => {
                  return (
                    <th
                      key={item}
                      id={`${item}-column`}
                      onClick={onColumnHeaderSort}
                      className={CX('column-header', columnName === item && 'selected')}
                    >
                      <span>{item}</span>
                      <SortDirectionIndicator header={item} sortKey={sortKey} />
                    </th>
                  )
                })}
                <th>
                  <span>Link</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {reviewList.map(item => {
                let listingUrl = `/listings/${market}/${item.marketAccountId}/bulk_review/${item.id}`
                if (!item.marketAccountId) {
                  listingUrl = `/listings/${market}/bulk_review/${item.id}` // we do not have Shopgoodwill's marketAccountId
                }

                return (
                  <tr key={item.id}>
                    <td>
                      <EmptyPlaceholder>
                        {item.sequence && (
                          <Link
                            to={{
                              pathname: listingUrl,
                              state: { listingIds, listingManagerUrl }
                            }}
                          >
                            {niceNumber(item.sequence)}
                          </Link>
                        )}
                      </EmptyPlaceholder>
                    </td>
                    <td>
                      <EmptyPlaceholder>{item.sku}</EmptyPlaceholder>
                    </td>
                    <td>
                      <EmptyPlaceholder>
                        <NiceListingStatus
                          value={item.state}
                          errors={
                            (market === marketAccounts.EBAY && item.ebayErrors) ||
                            (market === marketAccounts.SHOPIFY && item.shopifyErrors) ||
                            (market === marketAccounts.SHOPGOODWILL && item.sgwErrors)
                          }
                          customClassName={
                            [ListingStates.LISTING.id, ListingStates.MODIFYING.id].includes(item.state)
                              ? Classes.INTENT_SUCCESS
                              : ''
                          }
                        />
                      </EmptyPlaceholder>
                    </td>
                    <td>
                      <EmptyPlaceholder>{item.user.name}</EmptyPlaceholder>
                    </td>
                    <td>
                      {item.state === 'PENDING' || item.state === 'MODIFY_FAILED' || item.state === 'LISTING_FAILED' ? (
                        <Link
                          to={{
                            pathname: listingUrl,
                            state: { listingIds, listingManagerUrl }
                          }}
                        >
                          Edit
                        </Link>
                      ) : (
                        <EmptyPlaceholder>
                          {item.state === 'LISTED' && (
                            <ExternalChannelLink
                              id={item.externalMarketId}
                              listingUrl={marketName === CHANNELS.SHOPIFY.name ? item.listingUrl : null}
                              channel={marketName}
                              showCustomLink
                            />
                          )}
                        </EmptyPlaceholder>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </HTMLTable>
        )}
      </div>
    </Widget>
  )
}
